<template>
  <div>
    <!-- Hero -->
    <base-page-heading :title="itemData.name || itemData.itemDefinition">
      <template #extra>
        <b-breadcrumb class="breadcrumb-alt">
          <b-breadcrumb-item>
            <router-link to="/">{{ $t("layout.breadcrumb.home") }}</router-link>
          </b-breadcrumb-item>
          <b-breadcrumb-item>
            {{ $t("layout.breadcrumb.admin") }}
          </b-breadcrumb-item>
          <b-breadcrumb-item>
            <router-link to="/admin/items"
              >{{ $t("layout.breadcrumb.item") }}
            </router-link>
          </b-breadcrumb-item>
          <b-breadcrumb-item active
            >{{ itemData.name || itemData.itemDefinition }}
          </b-breadcrumb-item>
        </b-breadcrumb>
      </template>
    </base-page-heading>
    <!-- END Hero -->

    <!-- Remove Comparison Group Block Modal -->
    <b-modal
      id="modal-block-remove-group"
      body-class="p-0"
      hide-footer
      hide-header
    >
      <div class="block block-themed block-transparent mb-0">
        <div class="block-header bg-primary-dark">
          <h3 class="block-title">
            {{ $t("admin.itemData.modal.removeGroup.title") }}
          </h3>
          <div class="block-options">
            <button
              type="button"
              class="btn-block-option"
              @click="$bvModal.hide('modal-block-remove-group')"
            >
              <i class="fa fa-fw fa-times"></i>
            </button>
          </div>
        </div>
        <div class="block-content font-size-sm">
          <p>
            {{
              $t("admin.itemData.modal.removeGroup.text", {
                group: removeComparisonGroupData.name
              })
            }}
          </p>
        </div>
        <div class="block-content block-content-full text-right border-top">
          <b-button
            variant="light"
            size="sm"
            @click="$bvModal.hide('modal-block-remove-group')"
            >{{ $t("admin.itemData.modal.removeGroup.no") }}
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="removeComparisonGroup()"
          >
            <i class="fa fa-check mr-1"></i>
            {{ $t("admin.itemData.modal.removeGroup.yes") }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <!-- END Remove Comparison Group Block Modal -->

    <!-- Delete Block Modal -->
    <b-modal id="modal-block-delete" body-class="p-0" hide-footer hide-header>
      <div class="block block-themed block-transparent mb-0">
        <div class="block-header bg-primary-dark">
          <h3 class="block-title">
            {{ $t("admin.itemData.modal.deleteItem.title") }}
          </h3>
          <div class="block-options">
            <button
              type="button"
              class="btn-block-option"
              @click="$bvModal.hide('modal-block-delete')"
            >
              <i class="fa fa-fw fa-times"></i>
            </button>
          </div>
        </div>
        <div class="block-content font-size-sm">
          <p>
            {{
              $t("admin.itemData.modal.deleteItem.text", {
                item: itemData.name || itemData.itemDefinition
              })
            }}
          </p>
        </div>
        <div class="block-content block-content-full text-right border-top">
          <b-button
            variant="light"
            size="sm"
            @click="$bvModal.hide('modal-block-delete')"
          >
            {{ $t("admin.itemData.modal.deleteItem.no") }}
          </b-button>
          <b-button variant="primary" size="sm" @click="removeItem()">
            <i class="fa fa-check mr-1"></i>
            {{ $t("admin.itemData.modal.deleteItem.yes") }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <!-- END Delete Block Modal -->

    <!-- Delete Result Block Modal -->
    <b-modal
      id="modal-block-delete-result"
      body-class="p-0"
      hide-footer
      hide-header
    >
      <div class="block block-themed block-transparent mb-0">
        <div class="block-header bg-primary-dark">
          <h3 class="block-title">
            {{ $t("admin.itemData.modal.deleteResult.title") }}
          </h3>
          <div class="block-options">
            <button
              type="button"
              class="btn-block-option"
              @click="$bvModal.hide('modal-block-delete')"
            >
              <i class="fa fa-fw fa-times"></i>
            </button>
          </div>
        </div>
        <div class="block-content font-size-sm">
          <p>
            {{
              $t("admin.itemData.modal.deleteResult.text", {
                date: removeItemResultDate
              })
            }}
          </p>
        </div>
        <div class="block-content block-content-full text-right border-top">
          <b-button
            variant="light"
            size="sm"
            @click="$bvModal.hide('modal-block-delete-result')"
          >
            {{ $t("admin.itemData.modal.deleteResult.no") }}
          </b-button>
          <b-button variant="primary" size="sm" @click="removeItemResult()">
            <i class="fa fa-check mr-1"></i>
            {{ $t("admin.itemData.modal.deleteResult.yes") }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <!-- END Delete Result Block Modal -->

    <b-container class="content content-narrow">
      <b-row>
        <b-col xl="12">
          <base-block :title="$t('admin.itemData.masterdata.title')">
            <b-form-group
              label-cols-sm="4"
              :label="$t('admin.itemData.masterdata.competitor')"
              label-for="item-competitorId"
            >
              <select
                id="item-competitorId"
                v-model="itemData.competitorId"
                class="form-control"
              >
                <option
                  v-for="competitor in competitors"
                  v-bind:key="competitor.competitorId"
                  v-bind:value="competitor.competitorId"
                >
                  {{ competitor.name }}
                </option>
              </select>
            </b-form-group>
            <b-form-group
              label-cols-sm="4"
              :label="$t('admin.itemData.masterdata.name.title')"
              label-for="item-name"
            >
              <b-form-input
                id="item-name"
                type="text"
                :placeholder="$t('admin.itemData.masterdata.name.placeholder')"
                @keyup.enter="saveItem"
                v-model="itemData.name"
              />
            </b-form-group>
            <b-form-group
              label-cols-sm="4"
              :label="$t('admin.itemData.masterdata.itemDefinition.title')"
              label-for="search-itemDefinition"
            >
              <b-input-group>
                <b-form-input
                  id="search-itemDefinition"
                  type="text"
                  :placeholder="
                    $t('admin.itemData.masterdata.itemDefinition.placeholder')
                  "
                  @keyup.enter="saveItem"
                  v-model="itemData.itemDefinition"
                />
                <b-input-group-append v-if="itemData.itemLink">
                  <a
                    class="btn btn-alt-primary"
                    :href="itemData.itemLink"
                    target="_blank"
                  >
                    <i class="fas fa-external-link-alt"></i>
                  </a>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
            <b-form-group
              label-cols-sm="4"
              :label="$t('admin.itemData.masterdata.onlyFirstItem.title')"
              label-for="search-onlyFirstItem"
            >
              <select
                  id="search-onlyFirstItem"
                  v-model="itemData.onlyFirstItem"
                  class="form-control"
              >
                <option value="yes">
                  Ersten Artikel des Sets nutzen
                </option>
                <option value="second">
                  Zweiten Artikel des Sets nutzen
                </option>
                <option value="no">
                  Alle Artikel des Sets nutzen
                </option>
              </select>
            </b-form-group>
            <div class="form-group form-row" v-if="Auth.isUserManager()">
              <b-col xs="6" class="ml-auto text-left">
                <b-button
                  type="submit"
                  variant="danger"
                  v-b-modal.modal-block-delete
                >
                  {{ $t("admin.itemData.masterdata.button.delete") }}
                </b-button>
              </b-col>
              <b-col xs="6" class="ml-auto text-right">
                <b-button type="submit" variant="primary" @click="saveItem()">
                  {{ $t("admin.itemData.masterdata.button.save") }}
                </b-button>
              </b-col>
            </div>
          </base-block>
        </b-col>
      </b-row>
      <b-row>
        <b-col xl="12">
          <base-block :title="$t('admin.itemData.comparisonGroups.title')">
            <b-table-simple
              responsive
              bordered
              striped
              table-class="table-vcenter"
            >
              <b-thead>
                <b-tr>
                  <b-th></b-th>
                  <b-th class="text-center"
                    >{{
                      $t("admin.itemData.comparisonGroups.comparisonGroups")
                    }}
                  </b-th>
                  <b-th></b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr
                  v-for="(group, index) in comparisonGroups"
                  v-bind:key="index"
                >
                  <b-td>
                    <b-button
                      v-if="Auth.isUserManager()"
                      size="sm"
                      variant="alt-danger"
                      @click="removeComparisonGroupData = group"
                      v-b-modal.modal-block-remove-group
                    >
                      <i class="fa fa-fw fa-times"></i>
                    </b-button>
                  </b-td>
                  <b-td>
                    {{ group.name }}
                  </b-td>
                  <b-td>
                    <router-link
                      :to="
                        '/admin/comparison_groups/' +
                          group.comparisonGroupId +
                          '/'
                      "
                    >
                      <b-button size="sm" variant="alt-primary">
                        <i class="fa fa-fw fa-angle-right"></i>
                      </b-button>
                    </router-link>
                  </b-td>
                </b-tr>
                <b-tr v-if="comparisonGroups.length === 0">
                  <b-td colspan="3"
                    >{{ $t("admin.itemData.comparisonGroups.emptyMsg") }}
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </base-block>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <ul class="timeline timeline-alt timeline-centered">
            <li class="timeline-event">
              <div class="timeline-event-icon bg-success">
                <i class="far fa-calendar-check"></i>
              </div>
              <div class="timeline-event-block">
                <base-block>
                  <b-form-group
                    label-cols-sm="4"
                    :label="$t('admin.itemData.itemResult.date')"
                    label-for="item-name"
                  >
                    <b-form-input
                      id="item-result-date"
                      type="date"
                      :placeholder="
                        $t('admin.itemData.masterdata.name.placeholder')
                      "
                      @keyup.enter="saveItem"
                      v-model="addResult.date"
                    />
                  </b-form-group>
                  <b-form-group
                    label-cols-sm="4"
                    :label="$t('admin.itemData.itemResult.price')"
                    label-for="item-name"
                  >
                    <b-form-input
                      id="item-result-date"
                      type="number"
                      @keyup.enter="saveItem"
                      v-model="addResult.price"
                    />
                  </b-form-group>
                  <b-button
                    type="submit"
                    variant="secondary"
                    class="btn-block"
                    @click="addItemResult()"
                  >
                    {{ $t("admin.itemData.itemResult.add") }}
                  </b-button>
                  <br />
                </base-block>
              </div>
            </li>
            <li
              class="timeline-event"
              v-for="(itemResult, resultIndex) in itemResults"
              :key="resultIndex"
            >
              <div class="timeline-event-icon bg-success">
                <i class="far fa-calendar-check"></i>
              </div>
              <base-block
                :title="$d(new Date(itemResult.date), 'short')"
                header-bg
                class="timeline-event-block"
              >
                <div
                  class="alert alert-success d-flex align-items-center justify-content-between"
                  role="alert"
                >
                  <div class="flex-fill mr-3">
                    <p class="mb-0">{{ $n(itemResult.price, "currency") }}</p>
                  </div>
                  <div class="flex-00-auto">
                    <i
                      class="far fa-trash-alt"
                      style="cursor: pointer"
                      @click="
                        $bvModal.show('modal-block-delete-result');
                        removeItemResultDate = itemResult.date;
                      "
                    ></i>
                  </div>
                </div>
              </base-block>
            </li>
          </ul>
        </b-col>
      </b-row>
      <b-row v-if="showMoreResult">
        <b-col>
          <button @click="getItemResult" class="btn btn-block btn-primary">
            Weitere Einträge zeigen
          </button>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
// @ is an alias to /src

import Competitors from "@/assets/js/Competitors";
import Loading from "@/store/loading";
import Items from "@/assets/js/Items";
import ComparisonGroupsItems from "@/assets/js/ComparisonGroups/Items";
import Notification from "@/assets/js/Notification";
import Results from "@/assets/js/Items/Results";
import Auth from "@/assets/js/Auth";

export default {
  name: "itemData",
  components: {},
  data() {
    return {
      competitors: [],
      itemData: [],
      comparisonGroups: [],
      removeComparisonGroupData: {
        name: "",
        comparisonGroupId: ""
      },
      itemResults: [],
      showMoreResult: true,
      removeItemResultDate: "",
      addResult: {
        date: new Date().toISOString().split("T")[0],
        price: 0
      },
      Auth: Auth
    };
  },
  mounted() {
    Notification.init(this);

    Loading.dispatch("increase", 2);

    Competitors.get().then(competitors => {
      let competitorList = {};

      for (let i in competitors) {
        if (Object.prototype.hasOwnProperty.call(competitors, i)) {
          competitorList[competitors[i].competitorId] = competitors[i];
        }
      }

      this.$set(this, "competitors", competitorList);
      Loading.dispatch("reduce");
    });

    Items.getById(this.$route.params.itemId).then(itemData => {
      this.$set(this, "itemData", itemData);
      Loading.dispatch("reduce");
    });

    this.getItemResult();

    this.getComparisonGroups();
  },
  updated() {
    document.title = this.itemData.name || this.itemData.itemDefinition;
  },
  methods: {
    getItemResult: function() {
      Loading.dispatch("increase");
      Results.getByitem(
        this.$route.params.itemId,
        this.itemResults.length
      ).then(itemResults => {
        if (itemResults.length < 10) {
          this.showMoreResult = false;
        }

        this.$set(this, "itemResults", this.itemResults.concat(itemResults));
        Loading.dispatch("reduce");
      });
    },
    getComparisonGroups: function() {
      Loading.dispatch("increase");
      Items.getComparisonGroups(this.$route.params.itemId).then(
        comparisonGroups => {
          this.$set(this, "comparisonGroups", comparisonGroups);
          Loading.dispatch("reduce");
        }
      );
    },
    saveItem: function() {
      if (Auth.isUserManager()) {
        Loading.dispatch("increase");
        Items.update(
          this.itemData.id,
          this.itemData.competitorId,
          this.itemData.name,
          this.itemData.itemDefinition,
          this.itemData.onlyFirstItem
        ).then(result => {
          if (result === false) {
            Notification.push(
              "",
              "Artikel konnte nicht geändert werden",
              "danger"
            );
          } else {
            Notification.push("", "Artikel wurde geändert", "success");
          }

          Loading.dispatch("reduce");
        });
      }
    },
    removeItem: function() {
      if (Auth.isUserManager()) {
        this.$bvModal.hide("modal-block-delete");
        Loading.dispatch("increase");

        Items.remove(this.itemData.id).then(result => {
          Loading.dispatch("reduce");
          if (result === false) {
            Notification.push(
              "",
              "Artikel konnte nicht gelöscht werden",
              "danger"
            );
          } else {
            Notification.push("", "Artikel wurde gelöscht", "success");
            this.$router.push("/admin/items");
          }
        });
      }
    },
    removeItemResult: function() {
      if (Auth.isUserManager()) {
        this.$bvModal.hide("modal-block-delete-result");
        Loading.dispatch("increase");
        Results.addByDate(this.itemData.id, this.removeItemResultDate, 0).then(
          result => {
            Loading.dispatch("reduce");
            this.removeItemResultDate = "";

            if (result === false) {
              Notification.push(
                "",
                "Preiseintrag konnte nicht gelöscht werden",
                "danger"
              );
            } else {
              Notification.push("", "Preiseintrag wurde gelöscht", "success");
              this.itemResults = [];
              this.getItemResult();
            }
          }
        );
      }
    },
    addItemResult: function() {
      if (Auth.isUserManager()) {
        Loading.dispatch("increase");
        Results.addByDate(
          this.itemData.id,
          this.addResult.date,
          this.addResult.price
        ).then(result => {
          Loading.dispatch("reduce");
          this.removeItemResultDate = "";

          if (result === false) {
            Notification.push(
              "",
              "Preiseintrag konnte nicht erstellt werden",
              "danger"
            );
          } else {
            Notification.push("", "Preiseintrag wurde erstellt", "success");
            this.itemResults = [];
            this.addResult.price = 0
            this.addResult.date = new Date().toISOString().split("T")[0];
            this.getItemResult();
          }
        });
      }
    },
    removeComparisonGroup: function() {
      if (Auth.isUserManager()) {
        this.$bvModal.hide("modal-block-remove-group");
        Loading.dispatch("increase");

        ComparisonGroupsItems.removeItem(
          this.removeComparisonGroupData.comparisonGroupId,
          this.itemData.id
        ).then(result => {
          Loading.dispatch("reduce");
          if (result === false) {
            Notification.push(
              "",
              "Vergleichsgruppe konnte nicht entfernt werden",
              "danger"
            );
          } else {
            Notification.push("", "Vergleichsgruppe wurde entfernt", "success");
            this.getComparisonGroups();
          }
        });
      }
    }
  }
};
</script>

<style lang="scss"></style>
